import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import FormSkeleton from '../../../components/loaders/FormSkeleton';
import { useTranslation } from 'react-i18next';
import { IVehicleEditInfo } from '../types/interfaces';
import { customStyles } from '../../../utils/SelectStyles';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import EditVehicleImage from './EditVehicleImage/EditVehicleImage';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';

const EditVehicleForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t, i18n } = useTranslation();

  const { handleSubmit, submitLoading, apiData, getDataLoading } = useFormsIntegrationHelpers({
    queryKey: ['vehicles', id],
    id,
    singleGetApi: `/admin/vehicles/${id}`,
    editApi: `/admin/vehicles/${id}`,
    itemName: t('body_type'),
    listRoute: '/vehicles'
  });

  const validationSchema = Yup.object({});

  const initialValues: IVehicleEditInfo & { new_images: File[] } = {
    ...apiData,
    
    new_images: []
  };

  if (getDataLoading) return <FormSkeleton featuredSections={1} />;

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value });
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('model')}>
                <div className="inputs_group">
                  <FieldWrapper
                    customPadding
                    title={t('vehicle_make')}
                    inputPlaceholder={apiData?.make_model} //TODO: Change this to the real make key
                    options={[]}
                    selectStyle={customStyles}
                    select
                    disabled
                  />
                  <FieldWrapper
                    customPadding
                    title={t('vehicle_model')}
                    inputPlaceholder={apiData?.make_model}
                    inputName={`model`}
                    options={[]}
                    selectStyle={customStyles}
                    select
                    disabled
                  />
                </div>
              </SectionHeader>
              <SectionHeader title={t('variant')}>
                <div className="inputs_group">
                  <FieldWrapper title={t('name')} inputName={`variant.display_name`} inputPlaceholder={t('name')} input customPadding disabled />
                  <FieldWrapper
                    customPadding
                    title={t('fuel_type')}
                    inputPlaceholder={apiData?.variant?.fuel_type?.name}
                    inputName={`variant.fuel_type`}
                    options={[]}
                    selectStyle={customStyles}
                    select
                    disabled
                  />
                </div>
                <div className="inputs_group">
                  <FieldWrapper
                    customPadding
                    title={t('transmission')}
                    inputPlaceholder={apiData?.transmission?.name?.[i18n?.language as 'en' | 'ar']}
                    inputName={`variant.transmission`}
                    options={[]}
                    selectStyle={customStyles}
                    select
                    disabled
                  />
                  <FieldWrapper title={t('cc')} inputName={`variant.cc`} inputPlaceholder={t('cc')} input customPadding disabled />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('warranty_months')} inputName={`variant.warranty_months`} inputPlaceholder={t('warranty_months')} input customPadding disabled />
                  <FieldWrapper title={t('warranty_mileage')} inputName={`variant.warranty_mileage`} inputPlaceholder={t('warranty_mileage')} input customPadding disabled />
                </div>
              </SectionHeader>
              <SectionHeader title={t('colors')}>
                <div className="inputs_group">
                  <FieldWrapper
                    customPadding
                    title={t('exterior_color')}
                    inputPlaceholder={apiData?.exterior_color?.display_name?.[i18n?.language as 'en' | 'ar']}
                    inputName={`exterior_color`}
                    options={[]}
                    selectStyle={customStyles}
                    select
                    disabled
                  />
                  <FieldWrapper
                    customPadding
                    title={t('interior_color')}
                    inputPlaceholder={apiData?.interior_color?.display_name?.[i18n?.language as 'en' | 'ar']}
                    inputName={`interior_color`}
                    options={[]}
                    selectStyle={customStyles}
                    select
                    disabled
                  />
                </div>
              </SectionHeader>
              <SectionHeader title={t('advanced_settings')}>
                <div className="inputs_group" style={{ marginBottom: '2rem' }}>
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('hidden', value?.target?.checked)} active={formik.values.hidden} />
                    <p>
                      {t('hide_from_website')} <span className="side_note">({t('active_means_hide')})</span>
                    </p>
                  </div>
                </div>
              </SectionHeader>
              <EditVehicleImage title={t('gallery')} />
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditVehicleForm;
